import { useState } from 'react';
import PropTypes from 'prop-types';
import {
	Box,
	Typography,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Skeleton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import QuadPanel from 'features/IRWorkstation/components/Common/QuadPanel';
import FieldRow from 'features/IRWorkstation/components/Common/FieldRow';
import useInvestorCommitments from 'features/IRWorkstation/hooks/useInvestorCommitments';
import useInvestorCommitmentDetails from 'features/IRWorkstation/hooks/useInvestorCommitmentDetails';
import { useIRWorkstation } from 'features/IRWorkstation/contexts/IRWorkstationContext';
import { useTrackEvent } from 'hooks/useTrackEvent';
import { ButtonClickTargets } from 'constants/buttonClickTargets';
import { formatCurrency, formatPercent, formatDate } from 'utils/utils';

const headerTypographySx = {
	fontSize: '12px',
	fontWeight: 600,
	color: 'text.secondary',
	textTransform: 'uppercase',
	letterSpacing: '0.5px',
};

const cellTypographySx = {
	fontSize: '12px',
	color: 'text.primary',
};

const tableBorderSx = {
	borderBottom: '0.5px solid',
	borderColor: 'border.light',
};

const tableContainerSx = {
	border: '0.5px solid',
	borderColor: 'border.light',
	borderRadius: '8px',
	overflow: 'hidden',
};

const DataTable = ({ columns, rows, emptyMessage, renderRow }) => (
	<Box sx={tableContainerSx}>
		<Box
			sx={{
				display: 'grid',
				gridTemplateColumns: columns.map((c) => c.width).join(' '),
				gap: 1,
				backgroundColor: 'background.default',
				padding: 2,
				...tableBorderSx,
			}}
		>
			{columns.map((col) => (
				<Typography key={col.key} sx={headerTypographySx}>
					{col.label}
				</Typography>
			))}
		</Box>
		<Box>
			{rows.length === 0 ? (
				<Typography sx={{ fontSize: '12px', color: 'text.secondary', padding: 2 }}>
					{emptyMessage}
				</Typography>
			) : (
				<Box sx={{ display: 'flex', flexDirection: 'column' }}>
					{rows.map((row, index) => renderRow(row, index, rows.length))}
				</Box>
			)}
		</Box>
	</Box>
);

DataTable.propTypes = {
	columns: PropTypes.arrayOf(
		PropTypes.shape({
			key: PropTypes.string.isRequired,
			label: PropTypes.string.isRequired,
			width: PropTypes.string.isRequired,
		})
	).isRequired,
	rows: PropTypes.array.isRequired,
	emptyMessage: PropTypes.string.isRequired,
	renderRow: PropTypes.func.isRequired,
};

const InvestorCommitmentsCard = ({ accountId }) => {
	const { commitments, loading, error } = useInvestorCommitments(accountId);
	const { fetchDetails, getDetails, isLoading, getError } =
		useInvestorCommitmentDetails(accountId);
	const [expandedPanels, setExpandedPanels] = useState({});
	const { tenant } = useIRWorkstation();
	const { trackButtonClick } = useTrackEvent({ tenant, product: 'IEM' });

	const handleAccordionChange = async (investorId) => {
		const isExpanding = !expandedPanels[investorId];

		if (isExpanding) {
			trackButtonClick(ButtonClickTargets.LP360InvestorExpand);
		} else {
			trackButtonClick(ButtonClickTargets.LP360InvestorCollapse);
		}

		setExpandedPanels((prev) => ({
			...prev,
			[investorId]: isExpanding,
		}));

		if (isExpanding && !getDetails(investorId)) {
			await fetchDetails(investorId);
		}
	};

	const handleTransitionEnd = (event, investorId) => {
		if (expandedPanels[investorId]) {
			// Scroll the expanded accordion into view after animation completes
			const accordionElement = event.currentTarget.closest('.MuiAccordion-root');
			if (accordionElement) {
				accordionElement.scrollIntoView({
					behavior: 'smooth',
					block: 'nearest',
				});
			}
		}
	};

	const calculatePercentage = (contributed, committed) => {
		if (!committed || committed === 0) return 0;
		return (contributed / committed) * 100;
	};

	const renderCommitmentRow = (investor_commitment) => {
		const {
			id,
			entity,
			commitment,
			most_recent_capital_account,
			pro_rata,
		} = investor_commitment;

		const percentage = calculatePercentage(most_recent_capital_account?.contributions || 0, commitment);
		const isExpanded = expandedPanels[id] || false;
		const details = getDetails(id);
		const detailsLoading = isLoading(id);
		const detailsError = getError(id);

		return (
			<Accordion
				key={id}
				expanded={isExpanded}
				onChange={() => handleAccordionChange(id)}
				TransitionProps={{
					onEntered: (node) => handleTransitionEnd({ currentTarget: node }, id),
				}}
				sx={{
					'&:before': { display: 'none' },
					boxShadow: 'none',
					border: 'none',
					borderBottom: '0.5px solid',
					borderColor: 'border.light',
					'&.MuiAccordion-root': {
						margin: 0,
					},
				}}
			>
				<AccordionSummary
					expandIcon={<ExpandMoreIcon sx={{ fontSize: '20px' }} />}
					sx={{
						padding: '12px 14px',
						minHeight: 'unset',
						flexDirection: 'row-reverse',
						'&.Mui-expanded': {
							minHeight: 'unset',
						},
						'& .MuiAccordionSummary-expandIconWrapper': {
							marginRight: '12px',
							marginLeft: 0,
						},
						'& .MuiAccordionSummary-content': {
							margin: '0',
							alignItems: 'center',
							'&.Mui-expanded': {
								margin: '0',
							},
						},
					}}
				>
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
							width: '100%',
							gap: 2,
						}}
					>
						<Box sx={{ flex: 1, minWidth: 0 }}>
							<Typography
								sx={{
									fontSize: '13px',
									fontWeight: 700,
									color: 'text.primary',
									mb: '4px',
								}}
							>
								{entity?.name}
							</Typography>
							<Typography
								sx={{
									fontSize: '11px',
									color: 'text.secondary',
									lineHeight: 1.4,
								}}
							>
								Committed {formatCurrency(commitment, 'USD', { compact: true })} · Contributed{' '}
								{formatCurrency(most_recent_capital_account?.contributions || 0, 'USD', { compact: true })} (
								{formatPercent(percentage, { decimalPlaces: 0 })}) · Distributed{' '}
								{formatCurrency(most_recent_capital_account?.distributions || 0, 'USD', { compact: true })}
								{pro_rata && ` · Pro-Rata ${formatPercent(pro_rata)}`}
							</Typography>
						</Box>
						<Box sx={{ textAlign: 'right', flexShrink: 0 }}>
							<Typography
								sx={{
									fontSize: '13px',
									fontWeight: 700,
									color: 'text.primary',
									mb: '2px',
								}}
							>
								IRR {most_recent_capital_account?.irr ? formatPercent(most_recent_capital_account.irr) : 'N/A'}
							</Typography>
							<Typography
								sx={{
									fontSize: '11px',
									color: 'text.secondary',
								}}
							>
								{most_recent_capital_account?.effective_date ? `as of ${formatDate(most_recent_capital_account.effective_date, true)}` : ''}
							</Typography>
						</Box>
					</Box>
				</AccordionSummary>
				<AccordionDetails
					sx={{
						padding: '14px',
						backgroundColor: 'background.paper',
					}}
				>
					{detailsLoading ? (
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
							<Skeleton variant='rounded' height={120} />
							<Skeleton variant='rounded' height={120} />
							<Skeleton variant='rounded' height={120} />
							<Skeleton variant='rounded' height={120} />
						</Box>
					) : detailsError ? (
						<Typography color='error' sx={{ fontSize: '12px' }}>
							{detailsError}
						</Typography>
					) : details ? (
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
							{details.warnings && details.warnings.length > 0 && (
								<Typography sx={{ fontSize: '11px', color: 'warning.main', mb: 1 }}>
									Some data may be incomplete
								</Typography>
							)}
							{renderInvestorCommitmentDetails(investor_commitment)}
							{renderCapitalAccountStatement(most_recent_capital_account)}
							{renderTransactionsTable(details.transactions || [])}
							{renderTransfersTable(details.transfers || [])}
						</Box>
					) : null}
				</AccordionDetails>
			</Accordion>
		);
	};

	const renderFieldsSection = (title, fields) => (
		<Box sx={tableContainerSx}>
			<Box
				sx={{
					backgroundColor: 'background.default',
					padding: '8px 12px',
					...tableBorderSx,
				}}
			>
				<Typography sx={headerTypographySx}>
					{title}
				</Typography>
			</Box>
			<Box>
				{fields.map((field, index) => (
					<Box
						key={field.label}
						sx={{
							padding: '6px 14px',
							borderBottom: index < fields.length - 1 ? '0.5px solid' : 'none',
							borderColor: 'border.light',
						}}
					>
						<FieldRow label={field.label} value={field.value} />
					</Box>
				))}
			</Box>
		</Box>
	);

	const renderInvestorCommitmentDetails = (investorCommitment) => {
		// Commitment Date is a Salesforce Date ("YYYY-MM-DD"); format it in UTC so it
		// doesn't shift a day back in US timezones.
		const fields = [
			{ label: 'Investor Legal Name', value: investorCommitment.investor_legal_name || 'N/A' },
			{ label: 'Original Commitment', value: formatCurrency(investorCommitment.original_commitment) || 'N/A' },
			{ label: 'Commitment', value: formatCurrency(investorCommitment.commitment) || 'N/A' },
			{ label: 'Commitment Date', value: formatDate(investorCommitment.commitment_date, true) || 'N/A' },
			{ label: 'Closing Round', value: investorCommitment.closing_round || 'N/A' },
		];

		return renderFieldsSection('Investor Commitment Details', fields);
	};

	const renderCapitalAccountStatement = (capitalAccount) => {
		if (!capitalAccount) return null;

		const fields = [
			{ label: 'As of', value: capitalAccount.effective_date ? formatDate(capitalAccount.effective_date, true) : 'N/A' },
			{ label: 'Contributions', value: formatCurrency(capitalAccount.contributions) || '$0' },
			{ label: 'Distributions', value: formatCurrency(capitalAccount.distributions) || '$0' },
			{ label: 'IRR', value: capitalAccount.irr ? formatPercent(capitalAccount.irr) : 'N/A' },
		];

		return renderFieldsSection('Capital Account Statement', fields);
	};

	const transactionColumns = [
		{ key: 'type', label: 'Transaction', width: '2fr' },
		{ key: 'amount', label: 'Amount', width: '1fr' },
		{ key: 'date', label: 'Date', width: '1fr' },
	];

	const transferColumns = [
		{ key: 'transfer', label: 'Transfer', width: '2fr' },
		{ key: 'amount', label: 'Amount', width: '1fr' },
		{ key: 'date', label: 'Date', width: '1fr' },
	];

	const renderTransactionRow = (transaction, index, total) => (
		<Box
			key={transaction.id}
			sx={{
				display: 'grid',
				gridTemplateColumns: '2fr 1fr 1fr',
				gap: 1,
				padding: 2,
				borderBottom: index < total - 1 ? '0.5px solid' : 'none',
				borderColor: 'border.light',
			}}
		>
			<Typography sx={cellTypographySx}>{transaction.type || 'N/A'}</Typography>
			<Typography sx={cellTypographySx}>{formatCurrency(transaction.amount)}</Typography>
			<Typography sx={cellTypographySx}>{transaction.date ? formatDate(transaction.date, true) : 'N/A'}</Typography>
		</Box>
	);

	const renderTransferRow = (transfer, index, total) => {
		const directionIcon = transfer.direction === 'out' ? '↑' : '↓';
		const directionLabel = transfer.direction === 'out' ? 'Out' : 'In';
		const directionColor = transfer.direction === 'out' ? 'error.main' : 'success.main';
		const preposition = transfer.direction === 'out' ? 'to' : 'from';

		return (
			<Box
				key={transfer.id}
				sx={{
					display: 'grid',
					gridTemplateColumns: '2fr 1fr 1fr',
					gap: 1,
					padding: 2,
					borderBottom: index < total - 1 ? '0.5px solid' : 'none',
					borderColor: 'border.light',
				}}
			>
				<Typography sx={cellTypographySx}>
					{directionIcon}{' '}
					<Box component="span" sx={{ fontWeight: 700, color: directionColor }}>
						{directionLabel}
					</Box>{' '}
					{preposition} {transfer.other_investor?.name || 'Unknown'}
				</Typography>
				<Typography sx={cellTypographySx}>{formatCurrency(transfer.amount)}</Typography>
				<Typography sx={cellTypographySx}>{transfer.date ? formatDate(transfer.date, true) : 'N/A'}</Typography>
			</Box>
		);
	};

	const renderTransactionsTable = (transactions) => (
		<DataTable
			columns={transactionColumns}
			rows={transactions || []}
			emptyMessage="No transactions on record"
			renderRow={renderTransactionRow}
		/>
	);

	const renderTransfersTable = (transfers) => (
		<DataTable
			columns={transferColumns}
			rows={transfers || []}
			emptyMessage="No transfers on record"
			renderRow={renderTransferRow}
		/>
	);

	return (
		<Box
			data-testid='investor-commitments-card'
			sx={{ width: '100%', minWidth: 0, display: 'flex', '& > *': { flex: 1 } }}
		>
			<QuadPanel title='Investor Commitments'>
				{error ? (
					<Box sx={{ padding: '14px' }}>
						<Typography color='error' sx={{ fontSize: '12px' }}>
							Unable to load Investor Commitment records
						</Typography>
					</Box>
				) : loading ? (
					<Box sx={{ padding: '14px' }}>
						{Array.from({ length: 3 }).map((_, i) => (
							<Skeleton
								key={i}
								variant='rounded'
								height={60}
								sx={{ mb: i < 2 ? 1 : 0 }}
							/>
						))}
					</Box>
				) : commitments.length === 0 ? (
					<Box sx={{ padding: '14px' }}>
						<Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
							No commitments on record
						</Typography>
					</Box>
				) : (
					<Box>{commitments.map((commitment) => renderCommitmentRow(commitment))}</Box>
				)}
			</QuadPanel>
		</Box>
	);
};

InvestorCommitmentsCard.propTypes = {
	accountId: PropTypes.string.isRequired,
};

export default InvestorCommitmentsCard;
